import {
  DocumentTextIcon,
  ExclamationIcon,
  InformationCircleIcon,
  PlusCircleIcon,
} from "@heroicons/react/outline";
import { RiAlertFill, RiRefreshLine } from "@remixicon/react";
import BorderedPage from "components/BorderedPage";
import Button from "components/Button";
import MaxWidthPage from "components/MaxWidthPage";
import { FREE_PLAN_CUTOFF_DATE } from "pages/crawler/constants/crawlerConstants";
import DismissibleInfoBox from "components/DismissibleInfoBox";
import { PureTooltip } from "components/Tooltip";
import Spinner from "components/Spinner";
import { DataTable, Column } from "components/Table";
import CrawlerActionsDropdown from "pages/crawler/components/list/CrawlerActionsDropdown";
import { formatLastRun } from "pages/crawler/utils/crawlerDisplayUtils";
import { canUsePremium } from "utils/planUtils";
import { useCrawler } from "providers/CrawlerProvider";
import { RecentlyViewedProvider } from "providers/RecentlyViewed/RecentlyViewedProvider";
import { useNavigate } from "react-router-dom";
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  isAfter,
} from "date-fns";
import { getStatusColorClass } from "components/StatusBadge";
import { formatStatusDisplay, isStatusRunning } from "pages/crawler/utils/crawlerStatusUtils";
import CrawlerSortDropdown from "pages/crawler/components/list/CrawlerSortDropdown";
import { SearchInput } from "components/SearchInput";
import scraperApi from "api";
import Toaster from "components/Toaster";
import {
  mapCrawlerDataToFormData,
  mapFormDataToCrawlerData,
} from "pages/crawler/utils/crawlerFormUtils";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";

// status can be one of four
// Scheduled in x (D)
// Running
// Not running
// Done

// Error Component
const ErrorState = ({
  error,
  onRetry,
}: {
  error: string;
  onRetry: () => void;
}) => (
  <div className="flex flex-col items-center justify-center py-12 px-4">
    <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
      <RiAlertFill className="w-8 h-8 text-red-600" />
    </div>
    <h3 className="text-lg font-medium text-gray-900 mb-2">
      Something went wrong
    </h3>
    <p className="text-gray-600 text-center mb-6 max-w-md">
      {error || "We couldn't load your crawlers. Please try again."}
    </p>
    <Button
      text="Try Again"
      icon={{ Icon: RiRefreshLine }}
      onClick={onRetry}
      className="bg-red-600 hover:bg-red-700 text-white"
    />
  </div>
);

// Empty State (rendered inside the table body via DataTable's `empty` slot)
const EmptyState = ({ onCreateNew }: { onCreateNew: () => void }) => (
  <div className="flex flex-col items-center justify-center px-4">
    <div className="flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
      <DocumentTextIcon className="w-8 h-8 text-gray-400" />
    </div>
    <h3 className="text-lg font-medium text-gray-900 mb-2">No crawlers yet</h3>
    <p className="text-gray-600 text-center mb-6 max-w-md">
      Get started by creating your first crawler to begin collecting data from websites.
    </p>
    <Button
      text="Create Your First Crawler"
      onClick={onCreateNew}
      className="bg-blue-600 hover:bg-blue-700 text-white"
    />
  </div>
);

const formatNumber = (value: number | string | undefined): string => {
  if (value === undefined || value === null) return "-";
  const num = typeof value === "string" ? parseInt(value) : value;
  if (isNaN(num)) return "-";
  return num.toLocaleString();
};

const truncateUrl = (url: string, maxLength: number = 50): string => {
  if (!url || url.length <= maxLength) return url;
  return url.substring(0, maxLength) + "...";
};

// Helper functions to safely access crawler data
const getCrawlerValue = (crawler: any, key: string, fallback: any = "-") => {
  return crawler[key] || fallback;
};

// Helper function to calculate time until scheduled run using date-fns
const getScheduledTimeText = (
  supposedToRunAt: string | Date | null,
): string | null => {
  if (!supposedToRunAt) return null;

  const scheduledDate = new Date(supposedToRunAt);
  const now = new Date();

  // If the scheduled time has passed, don't show "scheduled in"
  if (!isAfter(scheduledDate, now)) return null;

  const days = differenceInDays(scheduledDate, now);
  const hours = differenceInHours(scheduledDate, now);
  const minutes = differenceInMinutes(scheduledDate, now);

  if (days > 0) {
    return `${days}d`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else if (minutes > 0) {
    return `${minutes}m`;
  } else {
    return "less than 1 minute";
  }
};

const CrawlerStatusBadge = ({ status, crawler }: { status: any; crawler: any }) => {
  // Normalize the status using our unified utility
  const { label, color, state } = formatStatusDisplay(status);

  // Handle special case for scheduled crawlers with time
  let displayLabel = label;
  if (state === "scheduled" && crawler.supposedToRunAt) {
    const timeText = getScheduledTimeText(crawler.supposedToRunAt);
    if (timeText) {
      displayLabel = `Scheduled in ${timeText}`;
    }
  }

  // Override status to running if crawler has running jobs
  const isRunning = crawler.hasRunningJobs || isStatusRunning(status);
  const finalState = isRunning ? "running" : state;
  const finalLabel = isRunning ? "Running" : displayLabel;

  // Replace "Scheduled in soon" with "Scheduled in < 1m"
  const finalDisplayLabel =
    finalLabel === "Scheduled in soon" ? "Scheduled in < 1m" : finalLabel;

  const colorClass = getStatusColorClass(color);

  return (
    <span className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md ${colorClass}`}>
      {finalState === "running" && (
        <Spinner size="XXS" theme="secondary" className="mr-1.5 !text-gray-800" />
      )}
      {finalDisplayLabel}
    </span>
  );
};

function PageTitle({ title }: { title: string }) {
  const { searchQuery, searchCrawlers} = useCrawler();
  const navigate = useNavigate();

  return (
    <div className="flex justify-between flex-wrap gap-4">
      <div className="text-2xl py-6 font-semibold">{title}</div>
      <div className="flex items-center gap-2 flex-wrap justify-end">
        <CrawlerSortDropdown defaultSort="lastRun" />
        <SearchInput
          placeholder="Search by name or URL"
          className="w-[250px] min-w-[100px]"
          searchValue={searchQuery}
          onSearchChange={searchCrawlers}
        />
        <Button
          text="NEW CRAWLER"
          className="bg-brandPrimary hover:brandPrimary-700 text-white rounded-md font-medium"
          icon={{ Icon: PlusCircleIcon }}
          onClick={() => navigate("/crawlers/new")}
        />
      </div>
    </div>
  );
}

const ViewAllCrawlersPage = () => {
  const {
    crawlers,
    loading,
    error,
    fetchCrawlers,
    loadMoreCrawlers,
    clearError,
    pagination,
    countGracePeriod,
  } = useCrawler();

  const { activeSubscription } = useActiveSubscription();
  const isFreePlan = activeSubscription && !canUsePremium(activeSubscription.planSlug);

  const showGracePeriodBanner = isFreePlan && countGracePeriod != null && 0 < countGracePeriod;

  const navigate = useNavigate();

  const handleRetry = () => {
    if (clearError) {
      clearError();
    }

    fetchCrawlers();
  };

  const handleCreateNew = () => navigate("/crawlers/new");

  const handleDisableScheduling = async (crawlerId: string) => {
    try {
      // Fetch the current crawler data
      const response = await scraperApi.crawlers.getCrawlerById(crawlerId);
      const crawler = response.data;

      // Convert crawler to form data, then back to crawler data with disabled scheduling
      // This ensures we use the same data transformation as the edit page
      const formData = mapCrawlerDataToFormData(crawler);

      // Update schedule to disabled
      formData.schedule = {
        ...formData.schedule,
        interval: "disabled",
        enabled: false,
        cron: "",
      };

      // Map back to crawler data format for the API
      const crawlerData = mapFormDataToCrawlerData(formData, crawler.name);

      await scraperApi.crawlers.updateCrawler(crawlerId, crawlerData);

      Toaster.success("Scheduling disabled successfully");

      // Refresh the crawlers list
      fetchCrawlers();
    } catch (error: any) {
      console.error("Failed to disable scheduling:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to disable scheduling";
      Toaster.error(errorMessage);
    }
  };

  const columns: Column<any>[] = [
    {
      key: "name",
      header: "Name",
      tooltip: "Name of the crawler",
      cell: (crawler) => getCrawlerValue(crawler, "name", "Unnamed Crawler"),
    },
    {
      key: "url",
      header: "URL",
      tooltip: "The url used in the crawler",
      cell: (crawler) => truncateUrl(getCrawlerValue(crawler, "domain", "No URL")),
    },
    {
      key: "jobs",
      header: "Jobs",
      tooltip: "The amount of jobs the crawler has executed",
      cell: (crawler) => formatNumber(getCrawlerValue(crawler, "jobs", 0)),
    },
    {
      key: "creditsSpent",
      header: "Credits spent",
      tooltip: "The amount of credits spent",
      cell: (crawler) => formatNumber(getCrawlerValue(crawler, "creditsSpent", 0)),
    },
    {
      key: "lastRun",
      header: "Last run",
      tooltip: "The last time the crawler ran",
      cell: (crawler) =>
        crawler.hasRunningJobs || crawler.status?.state === "running"
          ? "-"
          : formatLastRun(crawler.lastFinishedAt || crawler.lastStartedAt),
    },
    {
      key: "status",
      header: "Status",
      tooltip: "Current status of the crawler",
      cell: (crawler) => (
        <CrawlerStatusBadge status={getCrawlerValue(crawler, "status", "Unknown")} crawler={crawler} />
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (crawler) => (
        <CrawlerActionsDropdown
          crawlerId={crawler.id}
          status={crawler.status || ""}
          enabled={crawler.enabled && (!isFreePlan || showGracePeriodBanner)}
          onEdit={(id: string) => navigate(`/crawlers/${id}/edit`)}
          onDisableScheduling={handleDisableScheduling}
        />
      ),
    },
  ];

  const renderPage = () => {
    // Error state (page-level — rendered instead of the table)
    if (error && crawlers.length === 0) {
      return <ErrorState error={error} onRetry={handleRetry} />;
    }

    return (
      <div>
        <DataTable
          columns={columns}
          rows={crawlers || []}
          getRowKey={(crawler) => crawler.id}
          onRowClick={(crawler) => navigate(`/crawlers/${crawler.id}/jobs`)}
          isLoading={loading && crawlers.length === 0}
          empty={<EmptyState onCreateNew={handleCreateNew} />}
          className="min-w-[900px]"
        />
        {pagination.hasNextPage && (
          <div className="flex justify-end mt-2">
            <Button
              onClick={loadMoreCrawlers}
              className="button button-primary rounded-md"
              disabled={loading}
              text="LOAD MORE"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <MaxWidthPage fullWidth>
      <BorderedPage
        browserTitle="Crawlers"
        className="flex flex-col gap-4 mb-6"
      >
        <PageTitle title="Crawlers" />
        {showGracePeriodBanner && (
          <DismissibleInfoBox
            id="crawler-grace-period-banner-main"
            icon={<ExclamationIcon className="w-5 h-5 flex-shrink-0 text-amber-500" />}
          >
            <p>
              Your Crawler jobs are affected by upcoming plan changes. Some may stop running after{" "}
              <span className="font-semibold">{FREE_PLAN_CUTOFF_DATE}</span>.{" "}
              Review your jobs.
            </p>
          </DismissibleInfoBox>
        )}
        <div className="flex items-center gap-2 text-gray-600">
          <PureTooltip content="Results from each run are available for 7 days, then automatically deleted.">
            <InformationCircleIcon
              width={20}
              height={20}
              className="flex-shrink-0"
            />
          </PureTooltip>
          <p className="text-sm">
            Crawler results are stored for 7 days after the last run.
          </p>
        </div>
        <RecentlyViewedProvider>{renderPage()}</RecentlyViewedProvider>
      </BorderedPage>
    </MaxWidthPage>
  );
};

export default ViewAllCrawlersPage;
